import { useId, type ReactNode } from 'react'
import {
  AlertTriangle,
  CheckCircle2,
  CircleDashed,
  Info,
  MinusCircle,
  XCircle,
} from 'lucide-react'
import type { StatusKind } from '../types'

export function formatNumber(
  value: number | null | undefined,
  digits = 3,
  fallback = 'Missing',
): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return fallback
  const magnitude = Math.abs(value)
  if (magnitude !== 0 && (magnitude < 0.001 || magnitude >= 100000)) {
    return value.toExponential(Math.max(1, digits - 1))
  }
  return value.toLocaleString(undefined, { maximumFractionDigits: digits })
}

export function Section({
  title,
  description,
  actions,
  children,
  className = '',
}: {
  title: string
  description?: string
  actions?: ReactNode
  children: ReactNode
  className?: string
}) {
  return (
    <section className={`section ${className}`}>
      <div className="section-heading">
        <div>
          <h2>{title}</h2>
          {description ? <p>{description}</p> : null}
        </div>
        {actions ? <div className="section-actions">{actions}</div> : null}
      </div>
      {children}
    </section>
  )
}

export function Field({
  label,
  unit,
  hint,
  children,
  className = '',
}: {
  label: string
  unit?: string
  hint?: string
  children: ReactNode
  className?: string
}) {
  return (
    <label className={`field ${className}`}>
      <span className="field-label">
        {label}
        {unit ? <span className="unit">{unit}</span> : null}
      </span>
      {children}
      {hint ? <span className="field-hint">{hint}</span> : null}
    </label>
  )
}

export function NumberField({
  label,
  value,
  onChange,
  unit,
  hint,
  min,
  max,
  step = 'any',
  disabled = false,
  readOnly = false,
  error,
}: {
  label: string
  value: number | null
  onChange?: (value: number | null) => void
  unit?: string
  hint?: string
  min?: number
  max?: number
  step?: number | 'any'
  disabled?: boolean
  readOnly?: boolean
  error?: string
}) {
  const errorId = useId()

  return (
    <Field label={label} unit={unit} hint={hint}>
      <input
        type="number"
        value={value ?? ''}
        onChange={(event) => {
          if (!onChange) return
          const raw = event.target.value
          onChange(raw === '' ? null : Number(raw))
        }}
        min={min}
        max={max}
        step={step}
        disabled={disabled}
        readOnly={readOnly}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : undefined}
        className={readOnly ? 'read-only' : undefined}
      />
      {error ? <span id={errorId} className="field-error" role="alert">{error}</span> : null}
    </Field>
  )
}

export function SelectField({
  label,
  value,
  onChange,
  options,
  hint,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  options: Array<{ value: string; label: string }>
  hint?: string
}) {
  return (
    <Field label={label} hint={hint}>
      <select value={value} onChange={(event) => onChange(event.target.value)}>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </Field>
  )
}

export function ModelBadge({ children }: { children: ReactNode }) {
  return <span className="model-badge">{children}</span>
}

const statusMeta: Record<
  StatusKind,
  { icon: typeof CheckCircle2; label: string; className: string }
> = {
  'design-target': { icon: CircleDashed, label: 'Design target, not screened', className: 'neutral' },
  insufficient: { icon: MinusCircle, label: 'Nominal screen incomplete', className: 'neutral' },
  meets: { icon: CheckCircle2, label: 'Passes enabled nominal v1 checks', className: 'success' },
  marginal: { icon: AlertTriangle, label: 'Borderline on enabled nominal v1 checks', className: 'warning' },
  fails: { icon: XCircle, label: 'Fails one or more enabled nominal v1 checks', className: 'danger' },
}

export function StatusBadge({ status, label }: { status: StatusKind; label?: string }) {
  const meta = statusMeta[status]
  const Icon = meta.icon
  return (
    <span className={`status-badge ${meta.className}`}>
      <Icon aria-hidden="true" size={14} />
      {label ?? meta.label}
    </span>
  )
}

export function Notice({
  tone = 'info',
  title,
  children,
}: {
  tone?: 'info' | 'warning' | 'danger' | 'success'
  title?: string
  children: ReactNode
}) {
  const Icon =
    tone === 'warning' ? AlertTriangle : tone === 'danger' ? XCircle : tone === 'success' ? CheckCircle2 : Info
  return (
    <div className={`notice ${tone}`} role={tone === 'danger' ? 'alert' : 'status'}>
      <Icon aria-hidden="true" size={17} />
      <div>
        {title ? <strong>{title}</strong> : null}
        <div>{children}</div>
      </div>
    </div>
  )
}

export function Metric({
  label,
  value,
  unit,
  note,
}: {
  label: string
  value: ReactNode
  unit?: string
  note?: string
}) {
  return (
    <div className="metric">
      <span>{label}</span>
      <strong>
        {value} {unit ? <small>{unit}</small> : null}
      </strong>
      {note ? <em>{note}</em> : null}
    </div>
  )
}

export function EmptyState({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="empty-state">
      <CircleDashed aria-hidden="true" />
      <strong>{title}</strong>
      <p>{children}</p>
    </div>
  )
}
